import React, { useState, useEffect } from "react";
import apiClient from "../api/client";
import ChipInput from "../components/ChipInput";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

// ── Colours ───────────────────────────────────────────────────────────────────
const NAVY  = "#1B3A5C";
const TEAL  = "#0D7377";
const AMBER = "#F0A500";

// ── Sub-tab list ──────────────────────────────────────────────────────────────
const TABS = [
  { id: "renewals",     label: "Renewals" },
  { id: "new_contract", label: "New Contract" },
  { id: "compare",      label: "Compare Deals" },
];

// ── Allowed values — mirrors backend/app/routers/contracts.py's ALLOWED/COST_METRICS ──
const ALLOWED = {
  cost_type:            ["Data","Data & Streaming","Streaming","Tax","Levy","Match Tracker","Virtual Games","Web Content","Platform Fee"],
  cost_metric_type:     ["Per Bet","Cumulative","Per Event","Per Stream"],
  payment_structure:    ["Monthly","Quarterly","Annual"],
  payment_currency:     ["GBP","EUR","USD","AUD","CAD","SEK","NOK","DKK","ZAR","BRL","INR","JPY","HKD"],
  freebet_cost_type:    ["Excluded","Included","Not Applicable","Undefined"],
  inplay_cost_type:     ["All Bets","InPlay Only","Stream"],
  is_minimum_guarantee: ["Yes","No"],
  cost_selection_split: ["Not Applicable","Weighted","Relative","Undefined"],
  cost_deduction_type:  ["Not Applicable","Percentage","Fixed Value"],
  cumulative_structure: ["Not Applicable","Annual","Fiscal Year","Quarterly","Monthly"],
};

const COST_METRICS = {
  "Per Bet":    ["GGR","FreeBetGGR","PostTaxGGR","PostTaxFreeBetGGR","Turnover","Fixed Fee Per Payment Structure"],
  "Cumulative": ["GGR","FreeBetGGR","PostTaxGGR","PostTaxFreeBetGGR","Turnover"],
  "Per Event":  ["Fixed Fee Per Event","Fixed Fee Per Payment Structure"],
  "Per Stream": ["Fixed Fee Per Stream"],
};

const EMPTY_ROW = {
  contract_name: "", provider: "", provider_sub_tier: "Not Applicable",
  cost_type: "Data", start_date: "", end_date: "",
  payment_structure: "Annual", payment_currency: "GBP", domain_brand: "All",
  sport: "", league: "", event_country: "",
  freebet_cost_type: "Not Applicable", inplay_cost_type: "All Bets",
  cost_metric_type: "Per Bet", cost_metric: "GGR", cost_metric_amount: "",
  cumulative_range: "Not Applicable", cumulative_structure: "Not Applicable",
  cost_selection_split: "Not Applicable",
  cost_deduction_type: "Not Applicable", cost_deduction_amount: "Not Applicable",
  is_minimum_guarantee: "No", minimum_guarantee: "Not Applicable",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(val, decimals = 0) {
  if (val == null) return "—";
  return Number(val).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function fmtPct(val) {
  if (val == null) return "—";
  return `${Number(val).toFixed(2)}%`;
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-32">
      <div className="w-8 h-8 border-4 border-gray-200 rounded-full animate-spin"
        style={{ borderTopColor: TEAL }} />
    </div>
  );
}

function ErrorBox({ message }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm whitespace-pre-wrap">
      {message}
    </div>
  );
}

const inputClass = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white";

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function SelectField({ value, onChange, options }) {
  return (
    <select value={value || ""} onChange={e => onChange(e.target.value)} className={inputClass}>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function TextField({ value, onChange, placeholder, type = "text" }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={inputClass}
    />
  );
}

// ── Metric view toggle (mirrors Insights.js's) ────────────────────────────────
const METRIC_VIEWS = [
  { id: "standard", label: "Standard" },
  { id: "theo",     label: "Theo" },
  { id: "weighted", label: "Weighted" },
];

function MetricViewToggle({ value, onChange }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 font-medium mb-1">Metric View</label>
      <div className="flex bg-white border border-gray-300 rounded-lg overflow-hidden w-fit">
        {METRIC_VIEWS.map(v => (
          <button
            key={v.id}
            onClick={() => onChange(v.id)}
            className="px-3 py-1.5 text-sm font-medium transition-colors"
            style={value === v.id ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
          >
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── One proposed contract term row ────────────────────────────────────────────
function TermRowEditor({ row, onChange, onRemove, index, brandOptions }) {
  function set(field, value) {
    const updates = { [field]: value };
    // Keep cost_metric valid whenever cost_metric_type changes
    if (field === "cost_metric_type" && !COST_METRICS[value].includes(row.cost_metric)) {
      updates.cost_metric = COST_METRICS[value][0];
    }
    onChange(updates);
  }

  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-3 relative">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Row {index + 1}</span>
        <button onClick={onRemove} className="text-xs text-red-400 hover:text-red-600">Remove</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Contract Name" required><TextField value={row.contract_name} onChange={v => set("contract_name", v)} /></Field>
        <Field label="Provider" required><TextField value={row.provider} onChange={v => set("provider", v)} /></Field>
        <Field label="Provider Sub-Tier"><TextField value={row.provider_sub_tier} onChange={v => set("provider_sub_tier", v)} /></Field>
        <Field label="Cost Type" required><SelectField value={row.cost_type} onChange={v => set("cost_type", v)} options={ALLOWED.cost_type} /></Field>

        <Field label="Sport" required><TextField value={row.sport} onChange={v => set("sport", v)} placeholder="e.g. Tennis, or All" /></Field>
        <Field label="League"><TextField value={row.league} onChange={v => set("league", v)} placeholder="e.g. ATP, or All" /></Field>
        <Field label="Event Country"><TextField value={row.event_country} onChange={v => set("event_country", v)} placeholder="All" /></Field>
        <div className="relative">
          <Field label="Domain Brand" required>
            <ChipInput value={row.domain_brand} onChange={v => set("domain_brand", v)} options={brandOptions} placeholder="Type brand or 'All', comma for multiple..." />
          </Field>
        </div>

        <Field label="Start Date" required><TextField type="date" value={row.start_date} onChange={v => set("start_date", v)} /></Field>
        <Field label="End Date" required><TextField type="date" value={row.end_date} onChange={v => set("end_date", v)} /></Field>
        <Field label="Payment Structure" required><SelectField value={row.payment_structure} onChange={v => set("payment_structure", v)} options={ALLOWED.payment_structure} /></Field>
        <Field label="Payment Currency" required><SelectField value={row.payment_currency} onChange={v => set("payment_currency", v)} options={ALLOWED.payment_currency} /></Field>

        <Field label="Freebet Cost Type"><SelectField value={row.freebet_cost_type} onChange={v => set("freebet_cost_type", v)} options={ALLOWED.freebet_cost_type} /></Field>
        <Field label="Inplay Cost Type"><SelectField value={row.inplay_cost_type} onChange={v => set("inplay_cost_type", v)} options={ALLOWED.inplay_cost_type} /></Field>
        <Field label="Cost Selection Split"><SelectField value={row.cost_selection_split} onChange={v => set("cost_selection_split", v)} options={ALLOWED.cost_selection_split} /></Field>

        <Field label="Cost Metric Type" required><SelectField value={row.cost_metric_type} onChange={v => set("cost_metric_type", v)} options={ALLOWED.cost_metric_type} /></Field>
        <Field label="Cost Metric" required><SelectField value={row.cost_metric} onChange={v => set("cost_metric", v)} options={COST_METRICS[row.cost_metric_type] || []} /></Field>
        <Field label="Rate / Amount" required><TextField type="number" value={row.cost_metric_amount} onChange={v => set("cost_metric_amount", v)} placeholder="e.g. 0.07 for 7%" /></Field>

        {row.cost_metric_type === "Cumulative" && (
          <>
            <Field label="Cumulative Range"><TextField value={row.cumulative_range} onChange={v => set("cumulative_range", v)} placeholder="e.g. 0-500000" /></Field>
            <Field label="Cumulative Structure"><SelectField value={row.cumulative_structure} onChange={v => set("cumulative_structure", v)} options={ALLOWED.cumulative_structure} /></Field>
          </>
        )}

        <Field label="Deduction Type"><SelectField value={row.cost_deduction_type} onChange={v => set("cost_deduction_type", v)} options={ALLOWED.cost_deduction_type} /></Field>
        {row.cost_deduction_type !== "Not Applicable" && (
          <Field label="Deduction Amount"><TextField value={row.cost_deduction_amount} onChange={v => set("cost_deduction_amount", v)} /></Field>
        )}

        <Field label="Minimum Guarantee?"><SelectField value={row.is_minimum_guarantee} onChange={v => set("is_minimum_guarantee", v)} options={ALLOWED.is_minimum_guarantee} /></Field>
        {row.is_minimum_guarantee === "Yes" && (
          <Field label="MG Amount"><TextField value={row.minimum_guarantee} onChange={v => set("minimum_guarantee", v)} /></Field>
        )}
      </div>
    </div>
  );
}

// ── Renewals ───────────────────────────────────────────────────────────────────
function Renewals() {
  const [contractOptions, setContractOptions] = useState([]);
  const [selectedContract, setSelectedContract] = useState("");
  const [oldRows,    setOldRows]    = useState([]);
  const [loadingOld, setLoadingOld] = useState(false);

  const [proposedRows, setProposedRows] = useState([]);

  const [scenarioName,    setScenarioName]    = useState("");
  const [metricView,      setMetricView]      = useState("standard");
  const [projectionYears, setProjectionYears] = useState(3);
  const [brandOptions,    setBrandOptions]    = useState([]);
  const [brandAssumptions, setBrandAssumptions] = useState({});
  const [bulkGrowth, setBulkGrowth] = useState("");
  const [bulkMargin, setBulkMargin] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState(null);
  const [results,    setResults]    = useState(null);

  useEffect(() => {
    apiClient.get("/api/contracts/")
      .then(res => setContractOptions([...new Set(res.data.map(r => r.contract_name))].sort()))
      .catch(() => {});
    apiClient.get("/api/ref/brands")
      .then(res => {
        const brands = res.data.filter(b => b !== "All");
        setBrandOptions(brands);
        setBrandAssumptions(Object.fromEntries(
          brands.map(b => [b, { turnover_growth_pct: "5", margin_override_pct: "" }])
        ));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedContract) { setOldRows([]); setProposedRows([]); setResults(null); return; }
    setLoadingOld(true);
    apiClient.get(`/api/contracts/${encodeURIComponent(selectedContract)}`)
      .then(res => {
        setOldRows(res.data);
        setScenarioName(`${selectedContract} Renewal`);
        setProposedRows([]);
        setResults(null);
      })
      .catch(() => setOldRows([]))
      .finally(() => setLoadingOld(false));
  }, [selectedContract]);

  function startFromExisting() {
    setProposedRows(oldRows.map(r => {
      const { contract_row_id, ...rest } = r;
      return { ...rest };
    }));
  }

  function addBlankRow() {
    setProposedRows(rows => [...rows, { ...EMPTY_ROW, contract_name: selectedContract }]);
  }

  function updateRow(index, updates) {
    setProposedRows(rows => rows.map((r, i) => i === index ? { ...r, ...updates } : r));
  }

  function removeRow(index) {
    setProposedRows(rows => rows.filter((_, i) => i !== index));
  }

  function updateAssumption(brand, field, value) {
    setBrandAssumptions(a => ({ ...a, [brand]: { ...a[brand], [field]: value } }));
  }

  // Which brands actually need a growth assumption is driven by what the
  // proposed terms cover, not just "every brand in the system" — if none of
  // the proposed rows say 'All', only the brands actually mentioned (across
  // all rows, comma-separated values included) are relevant.
  const hasAllBrand = proposedRows.some(r =>
    (r.domain_brand || "").split(",").map(s => s.trim().toLowerCase()).includes("all")
  );
  const relevantBrands = (() => {
    if (proposedRows.length === 0) return [];
    if (hasAllBrand) return brandOptions;
    const mentioned = new Set();
    proposedRows.forEach(r => {
      (r.domain_brand || "").split(",").map(s => s.trim()).filter(Boolean).forEach(b => {
        const match = brandOptions.find(bo => bo.toLowerCase() === b.toLowerCase());
        mentioned.add(match || b);
      });
    });
    return brandOptions.filter(b => mentioned.has(b));
  })();

  function applyBulkToAll() {
    setBrandAssumptions(a => {
      const next = { ...a };
      relevantBrands.forEach(b => {
        next[b] = {
          turnover_growth_pct: bulkGrowth !== "" ? bulkGrowth : (next[b]?.turnover_growth_pct ?? "5"),
          margin_override_pct: bulkMargin !== "" ? bulkMargin : (next[b]?.margin_override_pct ?? ""),
        };
      });
      return next;
    });
  }

  const canSubmit = selectedContract && proposedRows.length > 0 && relevantBrands.length > 0 && !submitting;

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    setResults(null);
    try {
      const body = {
        scenario_name: scenarioName || `${selectedContract} Renewal`,
        baseline_contract_name: selectedContract,
        metric_view: metricView,
        projection_years: Number(projectionYears) || 3,
        brand_assumptions: relevantBrands.map(brand_label => {
          const a = brandAssumptions[brand_label] || {};
          return {
            brand_label,
            turnover_growth_pct: Number(a.turnover_growth_pct) || 0,
            margin_override_pct: a.margin_override_pct !== "" && a.margin_override_pct != null
              ? Number(a.margin_override_pct) : null,
          };
        }),
        proposed_terms: proposedRows.map(r => ({
          ...r,
          cost_metric_amount: r.cost_metric_amount === "" || r.cost_metric_amount == null
            ? null : Number(r.cost_metric_amount),
        })),
      };
      const res = await apiClient.post("/api/negotiations/scenarios", body);
      setResults(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail ? JSON.stringify(detail, null, 2) : "Failed to run scenario.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Step 1 — contract to renew */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h3 className="text-sm font-semibold mb-3" style={{ color: NAVY }}>1. Contract to Renew</h3>
        <div className="max-w-sm">
          <SelectField
            value={selectedContract}
            onChange={setSelectedContract}
            options={["", ...contractOptions]}
          />
        </div>

        {loadingOld ? <div className="mt-4"><LoadingSpinner /></div> : oldRows.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <p className="text-xs text-gray-400 mb-2">
              Full contract history shown for context — if this contract has been amended (e.g. a rate change), the scenario automatically uses only whichever row(s) are currently active, not the older superseded terms.
            </p>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  {["Sport","League","Cost Type","Metric","Rate","Brand","MG"].map(h => (
                    <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {oldRows.map((r, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                    <td className="px-3 py-2">{r.sport}</td>
                    <td className="px-3 py-2">{r.league}</td>
                    <td className="px-3 py-2">{r.cost_type}</td>
                    <td className="px-3 py-2">{r.cost_metric}</td>
                    <td className="px-3 py-2">{r.cost_metric_amount}</td>
                    <td className="px-3 py-2">{r.domain_brand}</td>
                    <td className="px-3 py-2">{r.is_minimum_guarantee === "Yes" ? r.minimum_guarantee : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Step 2 — proposed new terms */}
      {selectedContract && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-sm font-semibold" style={{ color: NAVY }}>2. Proposed New Terms</h3>
            <div className="flex gap-2">
              {oldRows.length > 0 && (
                <button onClick={startFromExisting}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">
                  Start from Existing Terms
                </button>
              )}
              <button onClick={addBlankRow}
                className="px-3 py-1.5 text-xs font-medium rounded-lg text-white"
                style={{ backgroundColor: TEAL }}>
                + Add Row
              </button>
            </div>
          </div>

          {proposedRows.length === 0 ? (
            <p className="text-gray-400 text-sm">No proposed terms yet — start from the existing contract or add a row.</p>
          ) : (
            <div className="space-y-4">
              {proposedRows.map((row, i) => (
                <TermRowEditor
                  key={i}
                  index={i}
                  row={row}
                  onChange={updates => updateRow(i, updates)}
                  onRemove={() => removeRow(i)}
                  brandOptions={brandOptions}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 3 — assumptions */}
      {selectedContract && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-sm font-semibold mb-1" style={{ color: NAVY }}>3. Growth Assumptions</h3>
          <p className="text-xs text-gray-400 mb-4">
            Years below (Y1, Y2, Y3…) always mean "the 12-month blocks following the most recent month of data" —
            they are not tied to whatever Start/End Date you typed on the proposed term rows above. A row dated
            2026-01-01 to 2026-12-31 still projects as "Y1", whatever the current data happens to end.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <Field label="Scenario Name">
              <TextField value={scenarioName} onChange={setScenarioName} />
            </Field>
            <Field label="Projection Years">
              <TextField type="number" value={projectionYears} onChange={setProjectionYears} />
            </Field>
            <MetricViewToggle value={metricView} onChange={setMetricView} />
          </div>

          {relevantBrands.length === 0 ? (
            <p className="text-gray-400 text-sm">Add a proposed term row with a Domain Brand set first — the relevant brands will appear here.</p>
          ) : (
            <>
              <div className="flex items-end gap-2 mb-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                <Field label="Apply to all brands — Growth %">
                  <TextField type="number" value={bulkGrowth} onChange={setBulkGrowth} placeholder="e.g. 5" />
                </Field>
                <Field label="Margin Override % (optional)">
                  <TextField type="number" value={bulkMargin} onChange={setBulkMargin} placeholder="Y0 actual" />
                </Field>
                <button onClick={applyBulkToAll}
                  className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-white whitespace-nowrap">
                  Apply to All
                </button>
              </div>

              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    {["Brand","Turnover Growth % (YoY)","Margin Override % (optional)"].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {relevantBrands.map((brand, i) => (
                    <tr key={brand} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                      <td className="px-3 py-2 font-medium">{brand}</td>
                      <td className="px-3 py-2 w-40">
                        <TextField type="number" value={brandAssumptions[brand]?.turnover_growth_pct}
                          onChange={v => updateAssumption(brand, "turnover_growth_pct", v)} />
                      </td>
                      <td className="px-3 py-2 w-48">
                        <TextField type="number" value={brandAssumptions[brand]?.margin_override_pct}
                          onChange={v => updateAssumption(brand, "margin_override_pct", v)} placeholder="Y0 actual" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Submit */}
      {selectedContract && (
        <div className="flex items-center gap-3">
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-6 py-2.5 rounded-lg text-sm font-medium text-white"
            style={{ backgroundColor: canSubmit ? TEAL : "#94a3b8", cursor: canSubmit ? "pointer" : "default" }}
          >
            {submitting ? "Running Scenario..." : "Run Scenario"}
          </button>
          {proposedRows.length === 0 && (
            <span className="text-xs text-gray-400">Add at least one proposed term row first</span>
          )}
        </div>
      )}

      {error && <ErrorBox message={error} />}

      {results && <ResultsView results={results} showTotalTab={hasAllBrand} />}
    </div>
  );
}

// ── Results ────────────────────────────────────────────────────────────────────
function y0Metric(y0Actual, metricView, base) {
  const suffix = metricView === "standard" ? "" : `_${metricView}`;
  return y0Actual?.[`${base}${suffix}`];
}

function ResultsView({ results, showTotalTab }) {
  const currencyNote = results.old_terms_currency !== results.new_terms_currency
    ? `Old terms in ${results.old_terms_currency}, new terms in ${results.new_terms_currency} — deltas aren't shown since they'd be comparing different currencies.`
    : `All figures in ${results.old_terms_currency}.`;

  // A single-brand contract only ever has one thing to show. A multi-brand
  // contract shows a tab per brand; only when the contract's coverage is
  // literally 'All' brands does a combined "Total" tab make sense too — a
  // contract explicitly scoped to a specific brand list is only ever those
  // brands' own numbers, not blended with brands it doesn't cover.
  const brandLabels = results.brands.map(b => b.brand_label);
  const tabs = brandLabels.length > 1 && showTotalTab ? [...brandLabels, "__total__"] : brandLabels;

  const [activeTab, setActiveTab] = useState(tabs[0]);
  useEffect(() => {
    setActiveTab(tabs[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  const activeEntry = activeTab === "__total__"
    ? results.combined
    : results.brands.find(b => b.brand_label === activeTab) || results.brands[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-semibold" style={{ color: NAVY }}>Results — {results.scenario_name}</h3>
        <span className="text-xs text-gray-400">
          Y0 window: {results.y0_window.start} → {results.y0_window.end} · Metric View: {results.metric_view}
        </span>
      </div>
      <p className="text-xs text-gray-400 -mt-4">{currencyNote}</p>
      {results.fx_rates_missing?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-lg p-3 text-xs">
          No FX rate found for: {results.fx_rates_missing.join(", ")} — those months were left unconverted, so figures involving them may be understated/overstated.
        </div>
      )}
      {tabs.length > 1 && (
        <div className="flex gap-1 bg-white rounded-xl shadow-sm border border-gray-100 p-1 w-fit flex-wrap">
          {tabs.map(t => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
              style={activeTab === t ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
            >
              {t === "__total__" ? "All / Total" : t}
            </button>
          ))}
        </div>
      )}
      {activeEntry && <BrandResultCard brand={activeEntry} metricView={results.metric_view} />}
    </div>
  );
}

const CHART_METRICS = [
  { id: "cost",          label: "Cost" },
  { id: "post_cost_ggr", label: "Post-Cost GGR" },
];

function BrandResultCard({ brand, metricView }) {
  const [chartMetric, setChartMetric] = useState("cost");

  const y0Cost = y0Metric(brand.y0_actual, metricView, "cost");
  const y0Ggr  = y0Metric(brand.y0_actual, metricView, "ggr");
  // Turnover only has Standard/Weighted variants (Theo turnover == Standard turnover, no separate column)
  const y0Turnover = metricView === "weighted" ? brand.y0_actual?.turnover_weighted : brand.y0_actual?.turnover;
  const y0PostCostGgr = y0Metric(brand.y0_actual, metricView, "post_cost_ggr");
  const y0RevShare = y0Ggr ? (y0Cost / y0Ggr) * 100 : null;

  const y0ChartValue = chartMetric === "cost" ? y0Cost : y0PostCostGgr;
  const chartData = [
    { year: "Y0 (Actual)", oldTerms: y0ChartValue, newTerms: y0ChartValue },
    ...brand.years.map(y => ({
      year: `Y${y.year}`,
      oldTerms: y.old_terms?.[chartMetric] ?? null,
      newTerms: y.new_terms?.[chartMetric] ?? null,
    })),
  ];
  const chartLabel = CHART_METRICS.find(m => m.id === chartMetric).label;

  const unsupported = new Set();
  brand.years.forEach(y => {
    (y.old_terms?.unsupported || []).forEach(u => unsupported.add(u));
    (y.new_terms?.unsupported || []).forEach(u => unsupported.add(u));
  });

  const mgTitle = "Minimum guarantee floor is driving this brand's cost — shared across every brand this contract covers, attributed here proportionally for display.";

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <h4 className="text-sm font-semibold mb-1" style={{ color: NAVY }}>{brand.brand_label}</h4>
      <p className="text-xs text-gray-400 mb-4">
        Y0 actual — {fmt(y0Turnover, 2)} turnover, {fmt(y0Ggr, 2)} GGR, {fmt(y0Cost, 2)} cost, {fmt(y0PostCostGgr, 2)} post-cost GGR ({fmtPct(y0RevShare)} rev share)
      </p>

      {unsupported.size > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-lg p-3 text-xs mb-4">
          Not modelled in this forecast: {[...unsupported].join(", ")} — see forecast.py for why.
        </div>
      )}

      <div className="flex justify-end mb-2">
        <div className="flex bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
          {CHART_METRICS.map(m => (
            <button
              key={m.id}
              onClick={() => setChartMetric(m.id)}
              className="px-3 py-1 text-xs font-medium transition-colors"
              style={chartMetric === m.id ? { backgroundColor: TEAL, color: "white" } : { color: "#6b7280" }}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="year" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmt(v)} />
          <Tooltip formatter={v => fmt(v, 2)} />
          <Legend />
          <Bar dataKey="oldTerms" fill={AMBER} name={`Old Terms ${chartLabel}`} radius={[4,4,0,0]} />
          <Bar dataKey="newTerms" fill={TEAL}  name={`New Terms ${chartLabel}`} radius={[4,4,0,0]} />
        </BarChart>
      </ResponsiveContainer>

      <div className="overflow-x-auto mt-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              {["Year","Turnover","GGR","Cost","Post-Cost GGR","Rev Share %","New Turnover","New GGR","New Cost","New Post-Cost GGR","New Rev Share %","Δ vs Old (same volume)","Δ vs Y0 Actual"].map(h => (
                <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="bg-gray-50">
              <td className="px-3 py-2 font-medium">Y0 (Actual)</td>
              <td className="px-3 py-2">{fmt(y0Turnover, 2)}</td>
              <td className="px-3 py-2">{fmt(y0Ggr, 2)}</td>
              <td className="px-3 py-2">{fmt(y0Cost, 2)}</td>
              <td className="px-3 py-2">{fmt(y0PostCostGgr, 2)}</td>
              <td className="px-3 py-2">{fmtPct(y0RevShare)}</td>
              {["—","—","—","—","—","—","—"].map((d, i) => (
                <td key={i} className="px-3 py-2 text-gray-300">{d}</td>
              ))}
            </tr>
            {brand.years.map((y, i) => (
              <tr key={y.year} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                <td className="px-3 py-2 font-medium">Y{y.year}</td>
                <td className="px-3 py-2">{fmt(y.old_terms?.turnover, 2)}</td>
                <td className="px-3 py-2">{fmt(y.old_terms?.ggr, 2)}</td>
                <td className="px-3 py-2">
                  {fmt(y.old_terms?.cost, 2)}
                  {y.old_terms?.mg_applied && (
                    <span className="ml-1 text-[10px] font-semibold text-amber-600" title={mgTitle}>MG</span>
                  )}
                </td>
                <td className="px-3 py-2">{fmt(y.old_terms?.post_cost_ggr, 2)}</td>
                <td className="px-3 py-2">{fmtPct(y.old_terms?.rev_share_pct)}</td>
                <td className="px-3 py-2">{fmt(y.new_terms?.turnover, 2)}</td>
                <td className="px-3 py-2">{fmt(y.new_terms?.ggr, 2)}</td>
                <td className="px-3 py-2">
                  {fmt(y.new_terms?.cost, 2)}
                  {y.new_terms?.mg_applied && (
                    <span className="ml-1 text-[10px] font-semibold text-amber-600" title={mgTitle}>MG</span>
                  )}
                </td>
                <td className="px-3 py-2">{fmt(y.new_terms?.post_cost_ggr, 2)}</td>
                <td className="px-3 py-2">{fmtPct(y.new_terms?.rev_share_pct)}</td>
                <td className="px-3 py-2">{fmt(y.delta_new_vs_old_same_volume, 2)}</td>
                <td className="px-3 py-2">{fmt(y.delta_new_vs_y0_actual, 2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Placeholder tabs ───────────────────────────────────────────────────────────
function ComingSoon({ label }) {
  return (
    <div className="flex items-center justify-center h-64">
      <p className="text-gray-400 text-lg">{label} — coming soon</p>
    </div>
  );
}

// ── Main Negotiations page ─────────────────────────────────────────────────────
export default function Negotiations() {
  const [activeTab, setActiveTab] = useState("renewals");

  const TAB_COMPONENTS = {
    renewals:     Renewals,
    new_contract: () => <ComingSoon label="New Contract" />,
    compare:      () => <ComingSoon label="Compare Deals" />,
  };
  const Component = TAB_COMPONENTS[activeTab];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: NAVY }}>Negotiations</h1>
        <p className="text-sm text-gray-500 mt-1">Model renewal and new-contract terms against real historical performance</p>
      </div>

      <div className="flex gap-1 mb-6 bg-white rounded-xl shadow-sm border border-gray-100 p-1 w-fit">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
            style={activeTab === tab.id
              ? { backgroundColor: TEAL, color: "white" }
              : { color: "#6b7280" }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <Component />
    </div>
  );
}
